import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, RotateCcw, Copy, Check, Download, Share2 } from "lucide-react";
import { Overline } from "../components/site/primitives";
import { generateFoundationCard } from "../components/site/essenceCard";

const API = process.env.REACT_APP_BACKEND_URL;

const FIRST_QUESTION = {
  type: "question",
  section: "pull",
  intro: "Let's start far away from job titles. We're not looking at your career today — we're looking for what pulls you in.",
  question: "What can you get completely lost in?",
  helper:
    "What do you genuinely love doing enough that time tends to disappear? Don't worry about whether it's useful, productive, impressive, or something people would pay for. Think about activities, conversations, problems, ideas, experiences, or kinds of creating that pull you in naturally.",
};

const SECTION_META = {
  pull: { label: "What Pulls You In", progress: 0.12 },
  natural: { label: "What Comes Naturally", progress: 0.28 },
  value: { label: "Outsized Value", progress: 0.44 },
  soughtfor: { label: "What People Come to You For", progress: 0.6 },
  essence: { label: "Essence Connection", progress: 0.76 },
  survival: { label: "Survival Mechanism Contrast", progress: 0.9 },
};

const THINKING = ["Listening...", "Looking underneath the activity...", "Following what feels suspiciously easy...", "Finding the ability under the doing..."];

const FINAL_SECTIONS = [
  { key: "why", title: "Why This Might Be It" },
  { key: "genius", title: "When You're in Your Genius" },
  { key: "pullsOut", title: "What Can Pull You Out of It" },
  { key: "wealth", title: "Why This Matters for Wealth" },
];

const fade = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
};

const Thinking = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % THINKING.length), 2400);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="sp-loading" className="flex min-h-[40vh] flex-col items-center justify-center text-center">
      <div className="h-px w-16 overflow-hidden bg-ink/10">
        <motion.div className="h-px w-16 bg-terracotta" animate={{ x: ["-100%", "100%"] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }} />
      </div>
      <AnimatePresence mode="wait">
        <motion.p
          key={idx}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.6 }}
          className="mt-8 font-display text-2xl font-medium italic leading-snug text-ink md:text-3xl"
        >
          {THINKING[idx]}
        </motion.p>
      </AnimatePresence>
    </div>
  );
};

const questionToText = (q) => [q.intro, q.question, q.helper].filter(Boolean).join("\n");

const loadStored = (key, count) => {
  try {
    const v = JSON.parse(localStorage.getItem(key) || "[]");
    if (Array.isArray(v) && v.length === count && v.every((x) => typeof x === "string")) return v;
  } catch {}
  return Array(count).fill("");
};

const buildCopyText = (final, essence, mechanisms) => {
  return [
    "MY SUPERPOWER",
    final.superpower,
    "",
    ...FINAL_SECTIONS.map((s) => `${s.title.toUpperCase()}\n${final[s.key]}`),
    "",
    "MY UNMISTAKABLE FOUNDATION",
    `ESSENCE\n${essence.join(" · ")}`,
    `SURVIVAL MECHANISMS\n${mechanisms.join("\n")}`,
    `SUPERPOWER\n${final.superpower}`,
    "",
    "Who you are. What tends to get in the way. What may become possible when you create from the best of what is already there.",
    "",
    "Discovered through Creating Wealth Through Community.",
  ].join("\n\n");
};

export default function Superpower() {
  const [stage, setStage] = useState("intro"); // intro | context | question | final
  const [essenceWords, setEssenceWords] = useState(() => loadStored("cwc_essence_words", 5));
  const [mechanisms, setMechanisms] = useState(() => loadStored("cwc_sm_names", 3));
  const [step, setStep] = useState(FIRST_QUESTION);
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [finalResult, setFinalResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [cardUrl, setCardUrl] = useState(null);
  const transcript = useRef([]);

  useEffect(() => {
    if (finalResult) {
      setCardUrl(null);
      generateFoundationCard(
        essenceWords.map((w) => w.trim()).filter(Boolean),
        mechanisms.map((m) => m.trim()).filter(Boolean),
        finalResult.superpower
      )
        .then(setCardUrl)
        .catch(() => setCardUrl(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalResult]);

  const downloadCard = () => {
    const a = document.createElement("a");
    a.href = cardUrl;
    a.download = "my-unmistakable-foundation.png";
    a.click();
  };

  const shareCard = async () => {
    try {
      const blob = await (await fetch(cardUrl)).blob();
      const file = new File([blob], "my-unmistakable-foundation.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My Unmistakable Foundation" });
        return;
      }
    } catch (e) {
      if (e.name === "AbortError") return;
    }
    downloadCard();
  };

  const contextReady = essenceWords.every((w) => w.trim()) && mechanisms.every((m) => m.trim());

  const callStep = async (messages) => {
    setError("");
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/superpower/step`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong.");
      transcript.current = messages;
      if (data.type === "final") {
        setFinalResult(data);
        setStage("final");
      } else {
        transcript.current = [...messages, { role: "guide", content: questionToText(data) }];
        setStep(data);
        setAnswer("");
        setStage("question");
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e.message || "We lost the thread for a moment. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const begin = () => {
    transcript.current = [
      {
        role: "participant",
        content: `[PARTICIPANT'S FIVE ESSENCE WORDS]: ${essenceWords.map((w) => w.trim()).join(", ")}\n[PARTICIPANT'S THREE SURVIVAL MECHANISMS]: ${mechanisms.map((m) => m.trim()).join(", ")}`,
      },
      { role: "guide", content: questionToText(FIRST_QUESTION) },
    ];
    setStep(FIRST_QUESTION);
    setAnswer("");
    setStage("question");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitAnswer = () => {
    if (answer.trim().length < 3 || busy) return;
    callStep([...transcript.current, { role: "participant", content: answer.trim() }]);
  };

  const restart = () => {
    transcript.current = [];
    setStage("intro");
    setStep(FIRST_QUESTION);
    setAnswer("");
    setFinalResult(null);
    setCardUrl(null);
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const copyResults = async () => {
    const text = buildCopyText(finalResult, essenceWords.map((w) => w.trim()), mechanisms.map((m) => m.trim()));
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const progress = SECTION_META[step.section]?.progress || 0.1;

  return (
    <div data-testid="sp-page" className="relative min-h-screen overflow-x-clip bg-ivory">
      <div className="grain-overlay" />
      <main className="relative mx-auto max-w-[860px] px-6 py-20 md:px-10 md:py-28">
        <AnimatePresence mode="wait">
          {stage === "intro" && (
            <motion.div key="intro" {...fade} data-testid="sp-intro">
              <Overline>Become Unmistakable</Overline>
              <h1 className="mt-6 font-display text-4xl font-medium leading-[1.02] tracking-tight text-ink sm:text-5xl lg:text-6xl">
                Discover Your <span className="italic text-terracotta">Superpower</span>
              </h1>
              <div className="mt-8 max-w-xl space-y-4 font-sans text-lg font-light leading-relaxed text-stonegray">
                <p>
                  In <span className="italic text-ink">The Big Leap</span>, Gay Hendricks describes four zones we can
                  operate from: Incompetence, Competence, Excellence, and Genius.
                </p>
                <p>
                  The Zone of Excellence is particularly sneaky because it's filled with things you're genuinely very
                  good at. You may even be successful because of them.
                </p>
                <p className="text-ink">But being good at something doesn't necessarily mean it's the highest and best use of you.</p>
                <p>
                  Your Zone of Genius points toward something deeper: what comes unusually naturally to you, energizes
                  you, creates disproportionate value, and allows more of who you actually are to come through.
                </p>
                <p className="font-display text-xl italic text-ink md:text-2xl">
                  We're going to identify a working hypothesis for yours. We're not figuring out your career today.
                  We're finding a <span className="text-terracotta">clue.</span>
                </p>
              </div>
              <button
                data-testid="sp-start-btn"
                onClick={() => {
                  setStage("context");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="group mt-10 inline-flex items-center gap-3 bg-terracotta px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-ink"
              >
                <span>Find My Superpower</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={1.6} />
              </button>
            </motion.div>
          )}

          {stage === "context" && (
            <motion.div key="context" {...fade} data-testid="sp-context">
              <Overline>What We Already Know</Overline>
              <h2 className="mt-6 font-display text-3xl font-medium leading-[1.05] tracking-tight text-ink sm:text-4xl md:text-5xl">
                Before we look for <span className="italic text-terracotta">your Superpower...</span>
              </h2>
              <p className="mt-6 max-w-xl font-sans text-lg font-light leading-relaxed text-stonegray">
                Let's bring forward what you've already distinguished. Edit anything that isn't right.
              </p>

              <p className="mt-10 font-sans text-xs font-medium uppercase tracking-[0.26em] text-terracotta">Your Essence</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {essenceWords.map((w, i) => (
                  <input
                    key={i}
                    data-testid={`sp-essence-input-${i + 1}`}
                    value={w}
                    onChange={(e) => {
                      const next = [...essenceWords];
                      next[i] = e.target.value;
                      setEssenceWords(next);
                    }}
                    placeholder={`Essence ${i + 1}`}
                    className="border-b border-ink/20 bg-transparent px-1 py-3 font-display text-xl text-ink placeholder:text-stonegray/40 focus:border-terracotta focus:outline-none"
                  />
                ))}
              </div>

              <p className="mt-10 font-sans text-xs font-medium uppercase tracking-[0.26em] text-terracotta">Your Survival Mechanisms</p>
              <div className="mt-4 space-y-3">
                {mechanisms.map((m, i) => (
                  <input
                    key={i}
                    data-testid={`sp-mechanism-input-${i + 1}`}
                    value={m}
                    onChange={(e) => {
                      const next = [...mechanisms];
                      next[i] = e.target.value;
                      setMechanisms(next);
                    }}
                    placeholder={`Survival Mechanism ${i + 1}`}
                    className="w-full border-b border-ink/20 bg-transparent px-1 py-3 font-display text-xl text-ink placeholder:text-stonegray/40 focus:border-terracotta focus:outline-none"
                  />
                ))}
              </div>

              <p className="mt-6 max-w-xl font-sans text-sm font-light leading-relaxed text-stonegray">
                Haven't done those yet? Start with{" "}
                <a href="/essence" className="text-terracotta underline underline-offset-4 hover:text-ink" data-testid="sp-essence-link">Discover Your Essence</a>
                {" "}and{" "}
                <a href="/survivalmechanism" className="text-terracotta underline underline-offset-4 hover:text-ink" data-testid="sp-sm-link">Meet Your Survival Mechanism</a>.
              </p>

              <button
                data-testid="sp-context-continue-btn"
                onClick={begin}
                disabled={!contextReady}
                className={`group mt-10 inline-flex items-center gap-3 px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] transition-[transform,background-color] duration-300 ${
                  contextReady ? "bg-terracotta text-white hover:-translate-y-0.5 hover:bg-ink" : "cursor-not-allowed bg-ink/10 text-ink/40"
                }`}
              >
                <span>Continue</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={1.6} />
              </button>
            </motion.div>
          )}

          {stage === "question" && !busy && (
            <motion.div key={`q-${transcript.current.length}`} {...fade} data-testid="sp-question">
              <Overline>{SECTION_META[step.section]?.label || "The Exercise"}</Overline>
              <div className="mt-4 h-px w-full bg-ink/10">
                <motion.div
                  className="h-px bg-terracotta"
                  initial={false}
                  animate={{ width: `${progress * 100}%` }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
              {step.section === "essence" && (
                <p data-testid="sp-essence-chips" className="mt-8 font-display text-lg font-medium text-terracotta md:text-xl">
                  {essenceWords.map((w) => w.trim()).filter(Boolean).join(" · ")}
                </p>
              )}
              {step.section === "survival" && (
                <p data-testid="sp-sm-chips" className="mt-8 font-display text-lg font-medium text-terracotta md:text-xl">
                  {mechanisms.map((m) => m.trim()).filter(Boolean).join(" · ")}
                </p>
              )}
              {step.intro && (
                <p className="mt-6 max-w-xl font-sans text-lg font-light leading-relaxed text-stonegray">{step.intro}</p>
              )}
              <h2 className="mt-6 whitespace-pre-line font-display text-3xl font-medium leading-[1.1] tracking-tight text-ink sm:text-4xl md:text-5xl">
                {step.question}
              </h2>
              {step.helper && (
                <p className="mt-5 max-w-xl border-l-2 border-terracotta/60 pl-4 font-sans text-sm font-light leading-relaxed text-stonegray">
                  {step.helper}
                </p>
              )}
              <textarea
                data-testid="sp-answer-input"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Answer casually. Imperfect is fine..."
                className="mt-8 min-h-[180px] w-full resize-y border border-ink/15 bg-alabaster/60 p-6 font-sans text-base font-light leading-relaxed text-ink placeholder:text-stonegray/60 focus:border-terracotta focus:outline-none"
              />
              {error && (
                <p data-testid="sp-error" className="mt-4 border-l-2 border-terracotta pl-4 font-sans text-sm font-light text-ink">{error}</p>
              )}
              <button
                data-testid="sp-continue-btn"
                onClick={submitAnswer}
                disabled={answer.trim().length < 3}
                className={`group mt-6 inline-flex items-center gap-3 px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] transition-[transform,background-color] duration-300 ${
                  answer.trim().length >= 3 ? "bg-terracotta text-white hover:-translate-y-0.5 hover:bg-ink" : "cursor-not-allowed bg-ink/10 text-ink/40"
                }`}
              >
                <span>Continue</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={1.6} />
              </button>
            </motion.div>
          )}

          {busy && (
            <motion.div key="busy" {...fade}>
              <Thinking />
            </motion.div>
          )}

          {stage === "final" && finalResult && !busy && (
            <motion.div key="final" {...fade} data-testid="sp-final">
              <Overline>Your Superpower</Overline>
              <motion.h2
                data-testid="sp-superpower"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="mt-8 font-display text-4xl font-medium leading-[1.08] tracking-tight text-ink sm:text-5xl md:text-6xl"
              >
                <span className="italic text-terracotta">{finalResult.superpower}</span>
              </motion.h2>

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1 }}>
                <div className="mt-14 space-y-12">
                  {FINAL_SECTIONS.map((s) => (
                    <div key={s.key}>
                      <h3 className="font-display text-2xl font-medium leading-snug text-ink md:text-3xl">{s.title}</h3>
                      <p data-testid={`sp-section-${s.key}`} className="mt-4 max-w-2xl font-sans text-lg font-light leading-relaxed text-stonegray">
                        {finalResult[s.key]}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="mt-20 border border-ink/12 bg-alabaster/60 p-8 md:p-10">
                  <h3 className="font-display text-2xl font-medium leading-snug text-ink md:text-3xl">Carry This With You</h3>
                  <div className="mt-4 max-w-2xl space-y-4 font-sans text-lg font-light leading-relaxed text-stonegray">
                    <p>This is a working hypothesis, not a verdict. We're not asking you to reorganize your life around it today.</p>
                    <p>
                      For now, notice it. Notice when you're doing it. Notice when people respond to it. Notice when it
                      feels almost too easy to count. And notice what pulls you away from it.
                    </p>
                    <p>
                      We're going to come back to this later in the Creating Wealth journey when we begin exploring
                      value, possibility, money, and what you may want to create.
                    </p>
                  </div>
                </div>

                <div className="mt-20 border-y border-ink/10 py-12 text-center" data-testid="sp-foundation">
                  <Overline>Your Unmistakable Foundation</Overline>
                  <div className="mt-10 space-y-10">
                    <div>
                      <p className="font-sans text-xs font-medium uppercase tracking-[0.26em] text-stonegray">Essence</p>
                      <p className="mt-3 font-display text-xl font-medium leading-relaxed text-ink md:text-2xl">
                        {essenceWords.map((w) => w.trim()).filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <div>
                      <p className="font-sans text-xs font-medium uppercase tracking-[0.26em] text-stonegray">Survival Mechanisms</p>
                      <div className="mt-3 space-y-1">
                        {mechanisms.map((m) => m.trim()).filter(Boolean).map((m) => (
                          <p key={m} className="font-display text-xl font-medium leading-relaxed text-ink/70 md:text-2xl">{m}</p>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="font-sans text-xs font-medium uppercase tracking-[0.26em] text-terracotta">Superpower</p>
                      <p className="mt-3 font-display text-xl font-medium italic leading-relaxed text-terracotta md:text-2xl">
                        {finalResult.superpower}
                      </p>
                    </div>
                  </div>
                  <p className="mx-auto mt-10 max-w-xl font-sans text-base font-light leading-relaxed text-stonegray">
                    Who you are. What tends to get in the way. What may become possible when you create from the best of
                    what is already there.
                  </p>
                </div>

                {cardUrl && (
                  <div data-testid="sp-card-section" className="mt-20">
                    <h3 className="font-display text-2xl font-medium leading-snug text-ink md:text-3xl">Your Foundation card</h3>
                    <p className="mt-3 max-w-xl font-sans text-base font-light leading-relaxed text-stonegray">
                      Everything you distinguished in Course 1 on one card — your Essence, your Survival Mechanisms,
                      and your Superpower. Save it somewhere you'll see it.
                    </p>
                    <div className="mt-8 grid items-start gap-10 md:grid-cols-2">
                      <img
                        src={cardUrl}
                        alt="Your Unmistakable Foundation card"
                        data-testid="sp-card-preview"
                        className="w-full max-w-sm shadow-[0_30px_70px_-30px_rgba(26,26,24,0.5)]"
                      />
                      <div className="flex flex-col items-start gap-4 md:pt-4">
                        <button
                          data-testid="sp-card-download-btn"
                          onClick={downloadCard}
                          className="group inline-flex items-center gap-3 bg-terracotta px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-ink"
                        >
                          <Download className="h-4 w-4" strokeWidth={1.6} />
                          <span>Download My Card</span>
                        </button>
                        <button
                          data-testid="sp-card-share-btn"
                          onClick={shareCard}
                          className="group inline-flex items-center gap-3 border border-ink/25 px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] text-ink transition-[transform,border-color,color] duration-300 hover:-translate-y-0.5 hover:border-terracotta hover:text-terracotta"
                        >
                          <Share2 className="h-4 w-4" strokeWidth={1.6} />
                          <span>Share My Card</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-12 flex flex-wrap items-center gap-4">
                  <button
                    data-testid="sp-copy-btn"
                    onClick={copyResults}
                    className="group inline-flex items-center gap-3 bg-terracotta px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-ink"
                  >
                    {copied ? <Check className="h-4 w-4" strokeWidth={1.8} /> : <Copy className="h-4 w-4" strokeWidth={1.6} />}
                    <span>{copied ? "Copied" : "Copy My Results"}</span>
                  </button>
                  <button
                    data-testid="sp-restart-btn"
                    onClick={restart}
                    className="group inline-flex items-center gap-3 border border-ink/25 px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] text-ink transition-[transform,border-color,color] duration-300 hover:-translate-y-0.5 hover:border-terracotta hover:text-terracotta"
                  >
                    <RotateCcw className="h-4 w-4" strokeWidth={1.6} />
                    <span>Start Again</span>
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
